import type { RootState } from "../state";
import { globalLiveRoomSyncWebSocket } from "@/utils/emitter/websocket/globalLiveRoomSyncWebSocket";
import { emitter } from "@/utils/emitter/mitt";
import { nextTick } from "vue";

/**
 * 初始化直播间同步功能
 */
export function initLiveRoomSync(this: RootState & { [key: string]: any }) {
  // 设置回调函数
  globalLiveRoomSyncWebSocket.setQueueUpdateCallback((roomId, queueData) => {
    this.handleQueueSync(roomId, queueData);
  });

  globalLiveRoomSyncWebSocket.setPublicMessageUpdateCallback(
    (roomId, messageData) => {
      this.handlePublicMessageSync(roomId, messageData);
    }
  );

  globalLiveRoomSyncWebSocket.setRoomStatusUpdateCallback(
    (roomId, statusData) => {
      this.handleRoomStatusSync(roomId, statusData);
    }
  );

  // 初始化跨浏览器监听器
  globalLiveRoomSyncWebSocket.initCrossBrowserListener();

  // 监听跨浏览器事件
  emitter.on("queueSync", data => {
    this.handleQueueSync(data.roomId, data);
  });

  emitter.on("publicMessageSync", data => {
    this.handlePublicMessageSync(data.roomId, data);
  });

  emitter.on("roomStatusSync", data => {
    this.handleRoomStatusSync(data.roomId, data);
  });

  emitter.on("roomFullSync", data => {
    this.handleRoomFullSync(data.roomId, data.roomData);
  });

  // 连接WebSocket
  // globalLiveRoomSyncWebSocket
  //   .connect()
  //   .then(() => {
  //     console.log("✅ 直播间同步 WebSocket 连接成功");
  //   })
  //   .catch(error => {
  //     console.error("❌ 直播间同步WebSocket连接失败:", error);
  //   });
}

/**
 * 处理队列同步
 */
export function handleQueueSync(
  this: RootState & { [key: string]: any },
  roomId: string,
  queueData: any
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法同步队列`);
    return;
  }

  // 🔧 修复：添加防重复调用机制
  const syncKey = `queueSync_${roomId}_${queueData.timestamp || Date.now()}`;
  if (this._lastQueueSyncKey === syncKey) {
    return; // 避免重复处理相同的同步事件
  }
  this._lastQueueSyncKey = syncKey;

  // 更新队列数据
  if (queueData.queue && Array.isArray(queueData.queue)) {
    state.queue = queueData.queue;
  }

  if (typeof queueData.currentIndex === "number") {
    state.currentIndex = queueData.currentIndex;
  }

  if (typeof queueData.isPlaying === "boolean") {
    state.isPlaying = queueData.isPlaying;
  }

  // 保存状态到本地存储
  this.saveStateToStorage();
}

/**
 * 处理公屏消息同步
 */
export function handlePublicMessageSync(
  this: RootState & { [key: string]: any },
  roomId: string,
  messageData: any
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法同步公屏消息`);
    return;
  }

  // 🔧 修复：添加防重复调用机制
  const syncKey = `messageSync_${roomId}_${messageData.timestamp || Date.now()}`;
  if (this._lastMessageSyncKey === syncKey) {
    return; // 避免重复处理相同的同步事件
  }
  this._lastMessageSyncKey = syncKey;

  // 更新公屏消息
  if (messageData.messages && Array.isArray(messageData.messages)) {
    state.publicMessages = messageData.messages;
  }

  // 如果有新消息，滚动到底部
  if (messageData.newMessage) {
    nextTick(() => {
      this.scrollToBottom();
    });
  }

  // 保存状态到本地存储
  this.saveStateToStorage();
}

/**
 * 处理房间状态同步
 */
export function handleRoomStatusSync(
  this: RootState & { [key: string]: any },
  roomId: string,
  statusData: any
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法同步状态`);
    return;
  }

  // 🔧 修复：添加防重复调用机制
  const syncKey = `roomStatusSync_${roomId}_${statusData.timestamp}`;
  if (this._lastSyncKey === syncKey) {
    return; // 避免重复处理相同的同步事件
  }
  this._lastSyncKey = syncKey;

  // 更新房间状态
  if (statusData.status && state.productList) {
    state.productList.status = statusData.status;
  }

  // 更新其他状态数据
  if (statusData.statusData) {
    Object.assign(state, statusData.statusData);
  }

  // 保存状态到本地存储
  this.saveStateToStorage();
}

/**
 * 处理房间完整同步
 */
export function handleRoomFullSync(
  this: RootState & { [key: string]: any },
  roomId: string,
  roomData: any
) {
  // 如果房间不存在，创建新房间状态
  if (!this.roomStates.has(roomId)) {
    this.roomStates.set(roomId, this.getDefaultRoomState());
  }

  const state = this.roomStates.get(roomId);

  // 更新房间的所有数据
  if (roomData) {
    Object.assign(state, roomData);
  }

  // 保存状态到本地存储
  this.saveStateToStorage();
}

/**
 * 发送队列状态到服务器
 */
export function sendQueueStatus(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法发送队列状态`);
    return;
  }

  const queueData = {
    queue: state.queue,
    currentIndex: state.currentIndex,
    isPlaying: state.isPlaying
  };

  globalLiveRoomSyncWebSocket.sendQueueStatus(roomId, queueData);
}

/**
 * 发送公屏消息到服务器
 */
export function sendPublicMessageStatus(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法发送公屏消息状态`);
    return;
  }

  const messageData = {
    messages: state.publicMessages,
    newMessage: false // 这里可以根据实际情况判断是否有新消息
  };

  globalLiveRoomSyncWebSocket.sendPublicMessage(roomId, messageData);
}

/**
 * 发送房间状态到服务器
 */
export function sendRoomStatus(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法发送房间状态`);
    return;
  }

  const statusData = {
    status: state.productList?.status,
    isPlaying: state.isPlaying,
    isBound: state.isBound,
    roomLink: state.roomLink,
    livePlatform: state.livePlatform,
    selectedVoiceId: state.selectedVoiceId,
    selectedProductIds: state.selectedProductIds
  };

  globalLiveRoomSyncWebSocket.sendRoomStatus(roomId, statusData);
}

/**
 * 请求房间完整同步
 */
export function requestRoomSync(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  globalLiveRoomSyncWebSocket.requestRoomSync(roomId);
}

/**
 * 清理直播间同步功能
 */
export function cleanupLiveRoomSync(this: RootState & { [key: string]: any }) {
  // 移除回调函数
  globalLiveRoomSyncWebSocket.removeAllCallbacks();

  // 移除事件监听器
  emitter.off("queueSync");
  emitter.off("publicMessageSync");
  emitter.off("roomStatusSync");
  emitter.off("roomFullSync");

  // 断开WebSocket连接
  globalLiveRoomSyncWebSocket.disconnect();
}

/**
 * 同步当前房间状态到其他平台
 */
export function syncCurrentRoomStatus(
  this: RootState & { [key: string]: any }
) {
  if (!this.currentRoomState) {
    console.warn("当前没有活跃的房间，无法同步状态");
    return;
  }

  const roomId =
    this.currentRoomState.roomId || this.currentRoomState.productList?.id;
  if (!roomId) {
    console.warn("房间ID不存在，无法同步状态");
    return;
  }

  // 发送房间状态
  this.sendRoomStatus(roomId);

  // 发送队列状态
  this.sendQueueStatus(roomId);

  // 发送公屏消息状态
  this.sendPublicMessageStatus(roomId);

  // 构建完整的状态数据
  const statusData = {
    roomId,
    status: this.currentRoomState.productList?.status,
    productList: this.currentRoomState.productList,
    publicMessages: this.currentRoomState.publicMessages,
    queue: this.currentRoomState.queue,
    statusData: {
      isPlaying: this.currentRoomState.isPlaying,
      isBound: this.currentRoomState.isBound,
      liveLink: this.currentRoomState.liveLink,
      livePlatform: this.currentRoomState.livePlatform,
      selectedVoiceId: this.currentRoomState.selectedVoiceId,
      selectedProductIds: this.currentRoomState.selectedProductIds
    },
    timestamp: Date.now()
  };

  // 写入localStorage用于跨浏览器同步
  try {
    localStorage.setItem("roomStatusSync", JSON.stringify(statusData));
  } catch (error) {
    console.error("写入localStorage失败:", error);
  }

  // 触发本地事件，确保其他组件能接收到状态更新
  emitter.emit("roomStatusSync", statusData);
}

/**
 * 监听队列变化并自动同步
 */
export function setupQueueAutoSync(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法设置队列自动同步`);
    return;
  }

  // 🔧 修复：使用深拷贝避免引用问题
  let lastQueueState = {
    queue: JSON.parse(JSON.stringify(state.queue || [])),
    currentIndex: state.currentIndex,
    isPlaying: state.isPlaying
  };

  // 创建防抖函数，避免频繁同步
  let syncTimer: any = null;
  const debouncedSync = () => {
    if (syncTimer) {
      clearTimeout(syncTimer);
    }
    syncTimer = setTimeout(() => {
      // 🔧 修复：只发送到服务器，不触发本地事件
      this.sendQueueStatus(roomId);
    }, 1000); // 1秒防抖
  };

  // 监听队列变化
  const checkQueueChanges = () => {
    const currentState = {
      queue: JSON.parse(JSON.stringify(state.queue || [])),
      currentIndex: state.currentIndex,
      isPlaying: state.isPlaying
    };

    // 🔧 修复：使用深比较避免误判
    const queueChanged =
      JSON.stringify(currentState.queue) !==
      JSON.stringify(lastQueueState.queue);
    const indexChanged =
      currentState.currentIndex !== lastQueueState.currentIndex;
    const playingChanged = currentState.isPlaying !== lastQueueState.isPlaying;

    if (queueChanged || indexChanged || playingChanged) {
      lastQueueState = currentState;
      debouncedSync();
    }
  };

  // 定期检查队列变化
  const intervalId = setInterval(checkQueueChanges, 2000);

  // 返回清理函数
  return () => {
    clearInterval(intervalId);
    if (syncTimer) {
      clearTimeout(syncTimer);
    }
  };
}

/**
 * 监听公屏消息变化并自动同步
 */
export function setupPublicMessageAutoSync(
  this: RootState & { [key: string]: any },
  roomId: string
) {
  const state = this.roomStates.get(roomId);
  if (!state) {
    console.warn(`房间${roomId}不存在，无法设置公屏消息自动同步`);
    return;
  }

  // 🔧 修复：使用深拷贝避免引用问题
  let lastMessagesLength = state.publicMessages.length;
  let lastMessagesHash = JSON.stringify(state.publicMessages);

  // 创建防抖函数
  let syncTimer: any = null;
  const debouncedSync = () => {
    if (syncTimer) {
      clearTimeout(syncTimer);
    }
    syncTimer = setTimeout(() => {
      // 🔧 修复：只发送到服务器，不触发本地事件
      this.sendPublicMessageStatus(roomId);
    }, 500); // 500ms防抖
  };

  // 监听消息变化
  const checkMessageChanges = () => {
    const currentLength = state.publicMessages.length;
    const currentHash = JSON.stringify(state.publicMessages);

    // 🔧 修复：使用长度和内容双重检查
    if (
      currentLength !== lastMessagesLength ||
      currentHash !== lastMessagesHash
    ) {
      lastMessagesLength = currentLength;
      lastMessagesHash = currentHash;
      debouncedSync();
    }
  };

  // 定期检查消息变化
  const intervalId = setInterval(checkMessageChanges, 1000);

  // 返回清理函数
  return () => {
    clearInterval(intervalId);
    if (syncTimer) {
      clearTimeout(syncTimer);
    }
  };
}
